import { Menu } from '@szhsin/react-menu';
import { pick } from 'lodash';
import moment from 'moment';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import {
    ButtonSize,
    ButtonVariant,
    Icon,
    IconName,
    Notes,
    Toast,
    Divider,
    getAgeFromDate,
    useDebouncedNoteUpdate,
} from '@catalyze/ui';
import { updateEDM } from 'api/edm/updateEdm';
import { useUpdateEdmStatusMutation } from 'api/edm/updateEdmStatus';
import { usePutNoteMutation } from 'api/leads/putNote';
import { updateLead } from 'api/leads/updateLead';
import type { TExpectedDecisionMaker } from 'api/types/edm';
import { EdmStatus } from 'api/types/edm';
import { Status } from 'api/types/lead';

import { ContactAddress } from './components/ContactAddress';
import { ContactEmails } from './components/ContactEmails';
import { ContactPhones } from './components/ContactPhones';
import {
    EditButton,
    Container,
    Name,
    MenuButton,
    StatusColor,
    StyledMenuItem,
    Relationship,
} from './styled';

interface IStaticEDM {
    onEditClick: () => void;
    data: TExpectedDecisionMaker;
    showDnc?: boolean;
}

export const StaticEDM: FC<IStaticEDM> = ({ onEditClick, data, showDnc }) => {
    const [note, setNote] = useState<string>('');
    const [prevStatus, setPrevStatus] = useState<EdmStatus>(data.status);
    const queryClient = useQueryClient();
    const { propertyId } = useParams<{ propertyId: string }>();

    useEffect(() => {
        setPrevStatus(data.status);
    }, [data]);

    const { mutate: putNote, isLoading: isLoadingNoteUpdate } =
        usePutNoteMutation({});

    const { activate, cancel: cancelDebounce } = useDebouncedNoteUpdate({
        note,
        callback: (callbackNote) => {
            if (callbackNote === data?.note) {
                return;
            }
            putNote({ id: Number(data.edmId), note: callbackNote });
        },
    });

    const onNotesBlur = () => {
        if (note === data?.note) {
            return;
        }
        cancelDebounce();
        putNote({ id: Number(data.edmId), note });
    };

    const onUpdateEdm = async (key: string, index: number) => {
        const clonedData = pick(data, [
            'dateOfBirth',
            'firstName',
            'lastName',
            'phones',
            'emails',
            'addresses',
            'status',
        ]);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        clonedData[key][index].updatedAt = moment().format('YYYY-MM-DD');
        clonedData.status = EdmStatus.CONTACTED;

        let description = '';
        if (key === 'phones') {
            description = clonedData[key][index].number;
        } else if (key === 'emails') {
            description = clonedData[key][index].value;
        } else if (key === 'addresses') {
            description = clonedData[key][index].address;
        }

        await updateEDM({ data: clonedData, id: data.edmId });

        if (propertyId) {
            await updateLead({
                id: parseInt(propertyId, 10),
                data: {
                    status: Status.CONTACTED,
                    description,
                },
            });
        } else {
            console.error('No Property ID found');
        }

        void queryClient.invalidateQueries('infinitePropertyLeads');
        void queryClient.invalidateQueries('property-lead');
        void queryClient.invalidateQueries('property-leads');
        void queryClient.invalidateQueries('property-leads-worth');

        toast(() => <Toast title="EDM updated!" />, {
            style: { backgroundColor: '#000' },
        });
    };

    // Activate debounce after local state is synced with incoming data
    useEffect(() => {
        if (data && (data.note === note || !data.note)) {
            activate();
        }
    }, [data, note, activate]);

    // Set local state on mount
    useEffect(() => {
        if (data) {
            data.note && setNote(data.note);
        }
    }, [data]);

    const { mutate: updateEdmStatus } = useUpdateEdmStatusMutation({
        onSuccess: async (data) => {
            await queryClient.invalidateQueries('property-lead');
            await queryClient.invalidateQueries('infinitePropertyLeads');
            await queryClient.invalidateQueries('property-leads');
            await queryClient.invalidateQueries('property-leads-worth');

            toast(
                () => (
                    <Toast
                        title={`EDM marked as ${data.status}`}
                        handleUndo={() => {
                            updateEdmStatus({
                                status: prevStatus,
                                id: data.edmId,
                            });
                        }}
                    />
                ),
                {
                    style: { backgroundColor: '#000' },
                }
            );
        },
    });

    const updatedString = (updatedAt: Date | undefined) => {
        if (updatedAt) {
            const timeDif = moment().diff(moment(updatedAt), 'days');
            if (timeDif === 0) {
                return 'Used today';
            } else if (timeDif === 1) {
                return `Used ${timeDif} day ago`;
            }
            return `Used ${timeDif} days ago`;
        }
        return '';
    };

    return (
        <>
            <Container>
                <EditButton onClick={onEditClick}>
                    <Icon iconName={IconName.EDIT} />
                </EditButton>
                {/* div because of flex alignment - Menu component creates extra element*/}
                <div>
                    <Menu
                        menuButton={() => (
                            <MenuButton
                                size={ButtonSize.SMALL}
                                variant={ButtonVariant.SECONDARY}
                            >
                                <StatusColor status={data.status} />
                                {data.status}
                                <Icon iconName={IconName.SORT} />
                            </MenuButton>
                        )}
                    >
                        {Object.values(EdmStatus).map((edmStatus) => (
                            <StyledMenuItem
                                key={edmStatus}
                                onClick={() => {
                                    setPrevStatus(data.status);
                                    updateEdmStatus({
                                        status: edmStatus,
                                        id: data.edmId,
                                    });
                                }}
                            >
                                <StatusColor status={edmStatus as EdmStatus} />
                                {edmStatus}
                            </StyledMenuItem>
                        ))}
                    </Menu>
                </div>
            </Container>
            <Name>{`${data.firstName} ${data.lastName} | Age: ${
                data.dateOfBirth ? getAgeFromDate(data.dateOfBirth) : ''
            }`}</Name>
            <Relationship>{data.relationship}</Relationship>
            <Divider />
            {data.phones?.map((phone, i) =>
                phone.number?.trim()?.length &&
                parseInt(phone.number?.trim(), 10) ? (
                    <ContactPhones
                        onClick={() => {
                            void onUpdateEdm('phones', i);
                        }}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${phone.number}_${i}`}
                        contactDetail={phone.number}
                        hasDoNotCall={Boolean(phone.dnc && showDnc)}
                        // eslint-disable-next-line max-len
                        contactUsed={updatedString(phone.updatedAt)}
                    />
                ) : null
            )}
            <Divider />
            {data.emails?.map((email, i) =>
                email.value?.trim()?.length &&
                parseInt(email.value?.trim(), 10) !== 0 ? (
                    <ContactEmails
                        key={email.value}
                        onClick={() => {
                            void onUpdateEdm('emails', i);
                        }}
                        contactDetail={email.value}
                        // eslint-disable-next-line max-len
                        contactUsed={updatedString(email.updatedAt)}
                    />
                ) : null
            )}
            <Divider />
            {data.addresses?.map((address, i) => (
                <ContactAddress
                    key={address.address}
                    addressFirstLine={address.address}
                    onClick={() => {
                        void onUpdateEdm('addresses', i);
                    }}
                    addressSecondLine={`${address.city}, ${address.state} ${address.zip}`}
                    // eslint-disable-next-line max-len
                    contactUsed={updatedString(address.updatedAt)}
                />
            ))}
            <Divider />
            <Notes
                value={note}
                onChange={setNote}
                onBlur={onNotesBlur}
                isLoading={isLoadingNoteUpdate}
                placeholder="Add Notes"
            />
        </>
    );
};

StaticEDM.defaultProps = {
    showDnc: false,
};
